/**
 * Upbit Binary Message Fix
 *
 * Problem: Upbit sends WebSocket messages as binary frames containing UTF-8 text.
 * CCXT Pro expects string messages, not ArrayBuffer/Blob.
 *
 * Solution: Patch WebSocket constructor to automatically decode binary messages to text.
 *
 * CRITICAL: This must be loaded BEFORE CCXT Pro creates any WebSocket connections.
 * Load this in main.tsx BEFORE any other imports.
 *
 * IMPORTANT: We only use onmessage (NOT addEventListener) to avoid the 15-30 second
 * connection death issue. See docs/WEBSOCKET-EVENT-HANDLING-LESSON.md
 */

/**
 * Patch WebSocket to handle Upbit binary text messages
 */
export function patchWebSocketForUpbit() {
  if (typeof WebSocket === 'undefined') {
    console.warn('[Upbit Binary Fix] WebSocket not available - skipping patch');
    return;
  }

  // Check if already patched
  if ((globalThis as any).__upbitWebSocketPatched) {
    console.info('[Upbit Binary Fix] Already patched - skipping');
    return;
  }

  console.info('[Upbit Binary Fix] Patching WebSocket for Upbit binary text messages...');

  // Store original WebSocket constructor
  const OriginalWebSocket = WebSocket;

  // Create patched WebSocket constructor
  const PatchedWebSocket = function(this: any, url: string | URL, protocols?: string | string[]) {
    // Call original constructor
    const ws = protocols
      ? new OriginalWebSocket(url, protocols)
      : new OriginalWebSocket(url);

    // Only patch Upbit WebSocket connections
    const urlStr = url.toString();
    if (urlStr.includes('upbit') || urlStr.includes('api-cloud.upbit.com')) {
      console.info('[Upbit Binary Fix] Patching Upbit WebSocket connection');

      // Set binaryType to arraybuffer for decoding
      ws.binaryType = 'arraybuffer';

      // Intercept onmessage property setter
      let userHandler: ((event: MessageEvent) => void) | null = null;

      Object.defineProperty(ws, 'onmessage', {
        get() {
          return userHandler;
        },
        set(handler: ((event: MessageEvent) => void) | null) {
          userHandler = handler;

          if (handler) {
            // Create wrapped handler that decodes binary messages
            const wrappedHandler = (event: MessageEvent) => {
              if (event.data instanceof ArrayBuffer) {
                // Decode binary to text
                const decoder = new TextDecoder('utf-8');
                const text = decoder.decode(event.data);

                // Create new event with decoded text
                const textEvent = new MessageEvent('message', {
                  data: text,
                  origin: event.origin,
                  lastEventId: event.lastEventId,
                  source: event.source,
                  ports: event.ports,
                });

                // Call user handler with decoded text
                handler.call(ws, textEvent);
              } else {
                // Pass through non-binary messages
                handler.call(ws, event);
              }
            };

            // Set the wrapped handler on the actual WebSocket
            Object.getOwnPropertyDescriptor(OriginalWebSocket.prototype, 'onmessage')!.set!.call(ws, wrappedHandler);
          } else {
            // Clear handler
            Object.getOwnPropertyDescriptor(OriginalWebSocket.prototype, 'onmessage')!.set!.call(ws, null);
          }
        },
        configurable: true,
      });
    }

    return ws;
  } as any;

  // Copy prototype and static properties
  PatchedWebSocket.prototype = OriginalWebSocket.prototype;
  PatchedWebSocket.CONNECTING = OriginalWebSocket.CONNECTING;
  PatchedWebSocket.OPEN = OriginalWebSocket.OPEN;
  PatchedWebSocket.CLOSING = OriginalWebSocket.CLOSING;
  PatchedWebSocket.CLOSED = OriginalWebSocket.CLOSED;

  // Replace global WebSocket
  (globalThis as any).WebSocket = PatchedWebSocket;
  (globalThis as any).__upbitWebSocketPatched = true;

  console.info('[Upbit Binary Fix] ✅ WebSocket patched - Upbit connections will decode binary text');
}

/**
 * Initialize the patch immediately when this module is imported
 */
patchWebSocketForUpbit();
